/**
 * Games-wide sound switch and the two sounds every board shares: a soft "ding-dong" when the
 * other player's move lands while you're watching, and a little pop for a chat message.
 * WebAudio only, no files. Best effort everywhere: blocked audio just means silence.
 */

const KEY = 'awwyeah.games.sound';

export function isGameSoundOn(): boolean {
  try {
    return window.localStorage.getItem(KEY) !== '0';
  } catch {
    return true;
  }
}

export function setGameSoundOn(on: boolean): void {
  try {
    window.localStorage.setItem(KEY, on ? '1' : '0');
  } catch {
    // lasts this visit only
  }
}

let ctx: AudioContext | null = null;

function audio(): AudioContext | null {
  try {
    if (!ctx) {
      const Ctx =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctx) return null;
      ctx = new Ctx();
    }
    if (ctx.state === 'suspended') void ctx.resume();
    return ctx.state === 'running' ? ctx : null;
  } catch {
    return null;
  }
}

/** Call from any tap so iPhones allow sounds later. */
export function unlockGameAudio(): void {
  audio();
}

function tone(ac: AudioContext, freq: number, at: number, length: number, peak: number): void {
  const osc = ac.createOscillator();
  const gain = ac.createGain();
  osc.type = 'sine';
  osc.frequency.value = freq;
  const t = ac.currentTime + at;
  gain.gain.setValueAtTime(0.0001, t);
  gain.gain.exponentialRampToValueAtTime(peak, t + 0.015);
  gain.gain.exponentialRampToValueAtTime(0.0001, t + length);
  osc.connect(gain);
  gain.connect(ac.destination);
  osc.start(t);
  osc.stop(t + length + 0.05);
}

/** The other player just moved. */
export function playMoveArrived(): void {
  if (!isGameSoundOn()) return;
  const ac = audio();
  if (!ac) return;
  try {
    tone(ac, 784, 0, 0.35, 0.14);
    tone(ac, 1047, 0.12, 0.45, 0.12);
  } catch {
    // never load-bearing
  }
}

/** A chat message arrived. */
export function playChatPop(): void {
  if (!isGameSoundOn()) return;
  const ac = audio();
  if (!ac) return;
  try {
    tone(ac, 1320, 0, 0.12, 0.1);
  } catch {
    // never load-bearing
  }
}
